
import React, { useState, useMemo, useCallback } from "react";
import { useFormikContext } from "formik";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import Button from "../../../components/ui/Button";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { SelectedDSPs } from "../../types/form";

interface PlatformSpecificUpc {
  id: string;
  dspName: string;
  upc: string;
  notes?: string;
}

interface PlatformSpecificUpcsProps {
  selectedDSPs: SelectedDSPs;
}

interface DeleteConfirmationState {
  show: boolean;
  entryId: string;
  dspName: string;
}

const DSP_MAPPING: Record<keyof SelectedDSPs, string> = {
  spotify: "Spotify",
  amazon: "Amazon Music",
  deezer: "Deezer",
  audiomack: "Audiomack",
  apple: "Apple Music",
  TIDAL: "TIDAL",
  youtube: "YouTube",
  soundcloud: "SoundCloud",
  email: "Email",
  siriusxm: "SiriusXM",
  vevo: "Vevo"
} as const;

const generateId = (): string =>
  `platform-upc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const MAX_NOTES_CHARACTERS = 300;

const PlatformSpecificUpcs: React.FC<PlatformSpecificUpcsProps> = ({ selectedDSPs }) => {
  const { values, setFieldValue } = useFormikContext<PitchFormValues>();

  // State management
  const [showForm, setShowForm] = useState(false);
  const [selectedDsp, setSelectedDsp] = useState("");
  const [upcValue, setUpcValue] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    show: false,
    entryId: "",
    dspName: ""
  });

  // Memoized computed values
  const currentUpcs = useMemo(() =>
    (values.platformSpecificUpcs || []) as PlatformSpecificUpc[],
    [values.platformSpecificUpcs]
  );

  const availableDsps = useMemo(() => {
    return Object.entries(selectedDSPs)
      .filter(([key, isSelected]) => isSelected && key !== 'email')
      .map(([key]) => DSP_MAPPING[key as keyof SelectedDSPs])
      .filter(Boolean);
  }, [selectedDSPs]);

  // Same DSP can be selected multiple times, so no filtering needed
  const canAdd = useMemo(() => availableDsps.length > 0, [availableDsps.length]);

  // Handlers
  const resetForm = useCallback(() => {
    setShowForm(false);
    setSelectedDsp("");
    setUpcValue("");
    setNotesValue("");
    setEditingId(null);
  }, []);

  // Close form if selected DSP is deselected from platform selection
  React.useEffect(() => {
    if (showForm && selectedDsp && !availableDsps.includes(selectedDsp)) {
      resetForm();
    }
  }, [showForm, selectedDsp, availableDsps, resetForm]);

  // Close form if no DSPs are available anymore
  React.useEffect(() => {
    if (showForm && availableDsps.length === 0) {
      resetForm();
    }
  }, [showForm, availableDsps.length, resetForm]);

  const handleAdd = useCallback(() => {
    setShowForm(true);
    setSelectedDsp("");
    setUpcValue("");
    setNotesValue("");
    setEditingId(null);
  }, []);

  const handleDspSelect = useCallback((dsp: string) => {
    setSelectedDsp(dsp);
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedDsp || !upcValue) return;

    if (editingId) {
      const updated = currentUpcs.map((entry) =>
        entry.id === editingId
          ? { ...entry, dspName: selectedDsp, upc: upcValue, notes: notesValue || undefined }
          : entry
      );
      setFieldValue("platformSpecificUpcs", updated);
    } else {
      const newEntry: PlatformSpecificUpc = {
        id: generateId(),
        dspName: selectedDsp,
        upc: upcValue,
        notes: notesValue || undefined,
      };
      setFieldValue("platformSpecificUpcs", [...currentUpcs, newEntry]);
    }

    resetForm();
  }, [selectedDsp, upcValue, notesValue, editingId, currentUpcs, setFieldValue, resetForm]);

  const handleEdit = useCallback((entryId: string) => {
    const entry = currentUpcs.find((e) => e.id === entryId);
    if (entry) {
      setSelectedDsp(entry.dspName);
      setUpcValue(entry.upc);
      setNotesValue(entry.notes || "");
      setEditingId(entryId);
      setShowForm(true);
    }
  }, [currentUpcs]);

  const handleDeleteClick = useCallback((entryId: string, dspName: string) => {
    setDeleteConfirmation({ show: true, entryId, dspName });
  }, []);

  const handleConfirmDelete = useCallback(() => {
    const updated = currentUpcs.filter((entry) => entry.id !== deleteConfirmation.entryId);
    setFieldValue("platformSpecificUpcs", updated);
    setDeleteConfirmation({ show: false, entryId: "", dspName: "" });
  }, [currentUpcs, deleteConfirmation.entryId, setFieldValue]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation({ show: false, entryId: "", dspName: "" });
  }, []);

  const handleUpcChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setUpcValue(value);
  }, []);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_NOTES_CHARACTERS) {
      setNotesValue(newValue);
    }
  }, []);

  return (
    <div className="mt-4 space-y-4">
      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-10 h-10 bg-danger-100 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-danger-600" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-neutral-900 mb-2">
                Delete Platform-Specific UPC
              </h3>
              <p className="text-sm text-neutral-500 mb-6">
                Are you sure you want to delete the platform-specific UPC for{" "}
                <span className="font-medium text-neutral-900">
                  {deleteConfirmation.dspName}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  type="button"
                  onClick={handleCancelDelete}
                  variant="neutral"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmDelete}
                  variant="danger"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          Platform-Specific UPCs
        </label>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          variant="soft"
          size="sm"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Platform-Specific UPC
        </Button>
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className="p-4 space-y-4 bg-neutral-50 border border-neutral-200 rounded-lg">
          {/* DSP Dropdown */}
          <div className="space-y-2">
            <label className="block mb-2 text-sm font-medium text-neutral-700">
              Select DSP
            </label>
            <Select value={selectedDsp} onValueChange={handleDspSelect}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a DSP" />
              </SelectTrigger>
              <SelectContent>
                {availableDsps.length > 0 ? (
                  availableDsps.map((dsp) => (
                    <SelectItem key={dsp} value={dsp}>
                      {dsp}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-3 text-sm text-neutral-500 text-center">
                    No platforms selected
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedDsp && (
            <>
              {/* UPC Input */}
              <div className="space-y-2">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  UPC
                </label>
                <input
                  type="text"
                  placeholder="Enter UPC"
                  value={upcValue}
                  onChange={handleUpcChange}
                  className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                />
              </div>

              {/* Notes Input */}
              <div className="space-y-2">
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Notes (Optional)
                </label>
                <div className="relative">
                  <textarea
                    placeholder="e.g., Waterfall release, Album UPC, Exclusive version"
                    value={notesValue}
                    onChange={handleNotesChange}
                    rows={2}
                    maxLength={MAX_NOTES_CHARACTERS}
                    className="w-full px-3 py-2 text-sm text-black bg-white border rounded-md outline-none transition-colors border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
                  />
                  <div className={`absolute bottom-2 right-3 text-xs ${notesValue.length >= MAX_NOTES_CHARACTERS ? 'text-danger-600 font-medium' : 'text-neutral-500'}`}>
                    {notesValue.length}/{MAX_NOTES_CHARACTERS}
                  </div>
                </div>
              </div>

              {/* Save/Cancel Buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={!upcValue}
                >
                  {editingId ? "Update UPC" : "Save UPC"}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="neutral"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Display Saved UPC Cards */}
      {currentUpcs.length > 0 && (
        <div className="space-y-3 mt-4">
          {currentUpcs.map((entry) => (
            <div
              key={entry.id}
              className="p-4 bg-white border border-neutral-200 rounded-lg shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-1">
                    {entry.dspName}
                  </h4>
                  <p className="text-sm text-neutral-700">
                    UPC: {entry.upc}
                  </p>
                  {entry.notes && (
                    <p className="text-sm text-neutral-500 mt-1">
                      {entry.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(entry.id)}
                    className="p-1 text-primary-700 hover:text-primary-800 focus:outline-none"
                    title="Edit UPC"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteClick(entry.id, entry.dspName)}
                    className="p-1 text-danger-600 hover:text-danger-800 focus:outline-none"
                    title="Delete UPC"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatformSpecificUpcs;
